// Stage 5 C-A — shared portal API helper.  Every endpoint is
// relative to /api/portal — audience-separated from /api/admin.
// Authentication rides on the `lrs_client_session` HttpOnly cookie,
// so callers just use withCredentials.
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND}/api/portal`;

axios.defaults.withCredentials = true;

export const portalApi = {
    login: (public_project_id, password) =>
        axios.post(`${API}/auth/login`, { public_project_id, password }),
    logout: () => axios.post(`${API}/auth/logout`),
    me: () => axios.get(`${API}/auth/me`),
    overview: () => axios.get(`${API}/overview`),
    programme: () => axios.get(`${API}/programme`),
    // `design` / `designIssue` / `feedbackDraft` / `feedbackAutosave` /
    // `feedbackSubmit` retired 2026-11 (Design Issue system full
    // retirement, Option A).  Replacement surface: /file-groups badge
    // fields + Overview `current_design_group`.
    files: (params) => axios.get(`${API}/files`, { params }),
    fileCategories: () => axios.get(`${API}/file-categories`),
    fileGroups: () => axios.get(`${API}/file-groups`),
    fileDetail: (id) => axios.get(`${API}/files/${id}`),
    filePreview: (id, params) =>
        axios.get(`${API}/files/${id}/preview`, { params }),
    fileVersions: (id) => axios.get(`${API}/files/${id}/versions`),
    fileDownloadUrl: (id) => `${API}/files/${id}/download`,
    fileVersionPreviewStreamUrl: (id, versionId) =>
        `${API}/files/${id}/preview-stream${
            versionId ? `?version_id=${encodeURIComponent(versionId)}` : ""}`,
    filesZip: (file_ids, versions) =>
        axios.post(`${API}/files/zip`, { file_ids, versions },
                   { responseType: "blob" }),
    actions: () => axios.get(`${API}/actions`),
    rfis: () => axios.get(`${API}/rfis`),
    rfiDetail: (id) => axios.get(`${API}/rfis/${id}`),
    rfiItemUpload: (rfiId, itemId, file) => {
        const fd = new FormData();
        fd.append("file", file);
        return axios.post(`${API}/rfis/${rfiId}/items/${itemId}/upload`, fd,
            { headers: { "Content-Type": "multipart/form-data" }});
    },
    rfiAutosave: (rfi_id, responses) =>
        axios.post(`${API}/rfis/autosave`, { rfi_id, responses }),
    rfiSubmit: (rfi_id, responses) =>
        axios.post(`${API}/rfis/submit`, { rfi_id, responses }),
    paidServices: () => axios.get(`${API}/paid-services`),
    package: (id) => axios.get(`${API}/paid-services/packages/${id}`),
    contract: (id) => axios.get(`${API}/paid-services/contracts/${id}`),
    contractChange: (id) =>
        axios.get(`${API}/paid-services/contract-changes/${id}`),
    certificate: (id) =>
        axios.get(`${API}/paid-services/certificates/${id}`),
    constructionProgress: () =>
        axios.get(`${API}/paid-services/construction-progress`),
    executedDocuments: (projectId) =>
        axios.get(`${API}/projects/${projectId}/executed-documents`),
    executedDocumentDownloadUrl: (fileId, versionId) => {
        const base = `${API}/executed-documents/${fileId}/download`;
        return versionId ? `${base}?version_id=${encodeURIComponent(versionId)}` : base;
    },
    accessStatus: () => axios.get(`${API}/access-status`),
};

export function fmtDate(v) {
    if (!v) return "—";
    try {
        const s = String(v).replace("Z", "+00:00");
        const d = new Date(s);
        if (isNaN(d)) return String(v).slice(0, 10);
        return d.toLocaleDateString("en-GB",
            { day: "2-digit", month: "short", year: "numeric" });
    } catch { return String(v).slice(0, 10); }
}

export function fmtMoney(v, currency = "GBP") {
    if (v == null) return "—";
    try {
        return new Intl.NumberFormat("en-GB",
            { style: "currency", currency, maximumFractionDigits: 0 })
            .format(v);
    } catch { return `£${v}`; }
}

export function daysUntil(v) {
    if (!v) return null;
    try {
        const d = new Date(String(v).replace("Z", "+00:00"));
        const diff = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
        return diff;
    } catch { return null; }
}

// Shared resolver — admin-authored `project_name` wins; otherwise fall
// back to the canonical two-client `client_name` computed server-side.
// Strict trim-equality on the "unset" check so whitespace-only values
// collapse to the fallback.  Used by:
//   • PortalOverview H1 (page header)
//   • PortalLayout sidebar project card (bottom of nav)
export function resolveProjectName(project) {
    if (!project) return "";
    const custom = (project.project_name || "").trim();
    if (custom) return custom;
    return (project.client_name || "").trim();
}
